import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define theme types
type ThemeMode = 'dark' | 'light';

// Define the shape of our theme context
interface ThemeContextType {
  theme: ThemeMode;
  toggleTheme: () => void;
  setTheme: (theme: ThemeMode) => void;
  isDarkMode: boolean;
}

// Create the context with a default value
const ThemeContext = createContext<ThemeContextType>({
  theme: 'dark', // Default to dark mode as per requirements
  toggleTheme: () => {},
  setTheme: () => {},
  isDarkMode: true,
});

// Custom hook to use the theme context
export const useTheme = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize theme state from localStorage or default to dark
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    // Check if a theme preference is stored
    const savedTheme = localStorage.getItem('snippix_theme');
    // Return the saved theme if valid, otherwise default to dark
    return savedTheme === 'light' || savedTheme === 'dark' ? savedTheme : 'dark';
  });

  // Computed property for checking if dark mode is active
  const isDarkMode = theme === 'dark';

  // Apply theme class to HTML element when theme changes
  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem('snippix_theme', theme);

    // Get the HTML element
    const htmlElement = document.documentElement;

    if (theme === 'dark') {
      // Add dark class for dark mode
      htmlElement.classList.add('dark');
    } else {
      // Remove dark class for light mode
      htmlElement.classList.remove('dark');
    }
  }, [theme]);

  // Function to toggle between dark and light modes
  const toggleTheme = () => {
    setThemeState(prevTheme => (prevTheme === 'dark' ? 'light' : 'dark'));
  };

  // Function to set a specific theme
  const setTheme = (newTheme: ThemeMode) => {
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme,
        setTheme,
        isDarkMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
